import React, { Suspense, useMemo } from 'react';
import { Spin } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import SiteLang from '@skygroup/shared/components/SiteLang';
import getImagePathFromName from '@skygroup/shared/utils/getImagePathFromName';
import importComponents from '../../utils/importComponents';
import classPriority from '../../utils/classPriority';
import useCssVariables from '../../hooks/useCssVariables';
import { updateLang, updateTranslations } from '../../store/config';
import translationsHe from '../../translations/translationsHe';
import translationsEn from '../../translations/translationsEn';
import translationsUk from '../../translations/translationsUk';
import './SupplierApprovalLayout.less';

interface Props {
  children: React.ReactNode;
}

/**
 * Layout for the public supplier-approval page. Renders a branded portal
 * header (logo + "Supplier Portal" tag, centered title/subtitle, a status
 * pill and the language selector) and the site's configured footer around
 * the page content.
 */
const SupplierApprovalLayout: React.FC<Props> = ({ children }) => {
  const dispatch = useDispatch();
  const generateCssVariables = useCssVariables();

  const {
    config: { layouts, modulesConfigs, siteConfig, theme },
    currentLang,
    direction,
    translations,
  } = useSelector((state: RootState) => state.config);

  const t: any = translations?.supplierApproval || {};

  const headerConfig = modulesConfigs?.Header;

  // Same locale switch the regular header uses.
  const changeLocale = (lang: string) => {
    dispatch(updateLang(lang));
    if (lang === 'en_US') {
      dispatch(updateTranslations(translationsEn.data.translation));
    } else if (lang === 'uk_UA') {
      dispatch(updateTranslations(translationsUk.data.translation));
    } else {
      dispatch(updateTranslations(translationsHe.data.translation));
    }
  };

  // Mirrors Header.tsx `renderSiteLang` exactly (same guard, props and styles).
  const renderSiteLang = ({
    wrapperClassName = '',
    withoutBorders = false,
  }: {
    wrapperClassName?: string;
    withoutBorders?: boolean;
  }) => {
    if (!headerConfig?.widgetLangs?.isActive) {
      return <></>;
    }
    return (
      <SiteLang
        languagesArr={siteConfig?.languages}
        defaultLang={currentLang ?? siteConfig?.defaultLang}
        changeLocale={changeLocale}
        wrapperClassName={wrapperClassName ?? 'header-site-lang'}
        withoutBorders={withoutBorders}
      />
    );
  };

  const footerComponents = useMemo(
    () => importComponents(layouts?.default?.bottomModules ?? [], modulesConfigs),
    [layouts, modulesConfigs]
  );

  return (
    <div className="supplier-approval-layout" dir={direction || 'rtl'}>
      <header className="sal-header">
        {/* Left: brand */}
        <div className="sal-header-brand">
          <span className="sal-logo">
            <img src={getImagePathFromName(siteConfig?.logo)} alt="logo" />
          </span>
          <span className="sal-portal-tag">
            {t.portalLabel || 'Supplier Portal'}
          </span>
        </div>

        {/* Center: title */}
        <div className="sal-header-title">
          <h1 className="sal-title">
            {t.headerTitle || 'Review & Approve Reservation Request'}
          </h1>
          <p className="sal-subtitle">
            {t.headerSubtitle ||
              'Review each room, confirm your price, enter your confirmation number, and approve.'}
          </p>
        </div>

        {/* Right: status + language */}
        <div className="sal-header-actions">
          <span className="sal-status-pill">
            <span className="sal-status-dot" />
            {t.pendingStatus || 'Pending Supplier Approval'}
          </span>
          {renderSiteLang({ withoutBorders: true })}
        </div>
      </header>

      <main className="sal-content">{children}</main>

      <Suspense fallback={<Spin className="center" size="large" />}>
        {footerComponents.map(({ Component, ComponentName }, i) => (
          <div className={ComponentName} key={`${ComponentName}-${i}`}>
            {modulesConfigs[ComponentName]?.theme &&
              generateCssVariables(
                `.${ComponentName}`,
                modulesConfigs[ComponentName].theme as any
              )}
            <Component
              config={modulesConfigs[ComponentName]}
              componentStyle={classPriority(theme, modulesConfigs[ComponentName])}
            />
          </div>
        ))}
      </Suspense>
    </div>
  );
};

export default SupplierApprovalLayout;
